import React, { useState } from 'react';
import { Check, Loader } from 'lucide-react';

/**
 * Les destinations d'un compte : son dashboard, les outils, le MCP.
 *
 * ## Pourquoi ce réglage existe
 *
 * Ajouter un compte le fait remonter partout à la fois. C'est le bon défaut — on ajoute un compte
 * pour s'en servir — mais un compte de test, une marque facturée à part ou un périmètre interne
 * n'ont rien à faire dans tous les écrans. Jusqu'ici, le seul geste disponible était de supprimer
 * le compte, ce qui l'effaçait de partout : exactement le contraire du besoin.
 *
 * ## Pourquoi le consolidé n'est pas ici
 *
 * Les comptes du dashboard consolidé ne sont pas choisis, ils sont **déduits du site** qui porte
 * la propriété analytique. C'est donc le site qui peut dire « rattaché, mais hors du total », et
 * le réglage vit dans Sites › Gérer les données. Les trois destinations ci-dessous, elles,
 * atteignent le compte par son identifiant sans lire aucun site : les débrayer vaut partout, et
 * les ranger avec le consolidé aurait laissé croire qu'elles se règlent périmètre par périmètre.
 *
 * ## Aucun repli
 *
 * Le composant s'ouvre directement sur ses cases. Il vivait auparavant derrière un bouton-repère,
 * hérité de l'époque où il partageait une carte avec d'autres réglages : la ligne de liste ayant
 * déjà son propre bouton, ce repli imposait deux clics pour cocher une case. Une seule façon
 * d'ouvrir, et elle est dans la liste.
 */

const DESTINATIONS = [
  { cle: 'actifDashboards', libelle: 'son dashboard', aide: 'Le compte reste sélectionnable dans son dashboard de connecteur.' },
  { cle: 'actifOutils', libelle: 'les outils', aide: 'Diagnostic, Radar, opportunités de mots-clés, enchères…' },
  { cle: 'actifMcp', libelle: 'le MCP', aide: 'Le compte est listé par le connecteur MCP dans Claude.' }
];

const ChampUsagesCompte = ({ item, token, onEnregistre }) => {
  const [occupe, setOccupe] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [etat, setEtat] = useState({
    actifDashboards: item.actifDashboards !== false,
    actifOutils: item.actifOutils !== false,
    actifMcp: item.actifMcp !== false
  });

  const basculer = async (cle) => {
    const valeur = !etat[cle];
    setOccupe(cle);
    setErreur(null);

    // L'état bascule tout de suite : attendre le serveur ferait clignoter la case sous le doigt.
    setEtat(precedent => ({ ...precedent, [cle]: valeur }));

    try {
      const reponse = await fetch(`/api/library/${item.databaseId || item.id}/usages`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ [cle]: valeur })
      });
      const donnees = await reponse.json();
      if (!reponse.ok || !donnees.success) throw new Error(donnees.error || 'Échec');

      if (onEnregistre) onEnregistre();
    } catch (e) {
      // Le serveur a refusé : on remet la case où elle était, sinon l'écran ment.
      setEtat(precedent => ({ ...precedent, [cle]: !valeur }));
      setErreur(e.message);
    } finally {
      setOccupe(null);
    }
  };

  return (
    <div className="usages-saisie" onClick={(e) => e.stopPropagation()}>
      <p className="usages-titre">Destinations</p>
      {DESTINATIONS.map(destination => (
        <label key={destination.cle} className="usages-ligne" title={destination.aide}>
          <input
            type="checkbox"
            checked={etat[destination.cle]}
            disabled={occupe === destination.cle}
            onChange={() => basculer(destination.cle)}
          />
          <span>{destination.libelle}</span>
          {occupe === destination.cle
            ? <Loader size={12} className="usages-tourne" />
            : etat[destination.cle] && <Check size={12} className="usages-coche" />}
        </label>
      ))}
      <p className="usages-aide">
        Les dashboards consolidés se règlent site par site, dans Sites › Gérer les données.
      </p>
      {erreur && <p className="usages-erreur">{erreur}</p>}
    </div>
  );
};

export default ChampUsagesCompte;
